<template>
  <section class="bg-gray-100 rounded-lg p-4 sm:p-6">
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
      <h2 class="text-xl font-semibold text-gray-800">Categorías de Noticias</h2>
      <button
        type="button"
        class="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 self-start sm:self-auto"
        @click="openForm(null)"
      >
        + Nueva Categoría
      </button>
    </div>

    <p v-if="error" class="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{{ error }}</p>

    <p v-if="loading" class="py-8 text-center text-sm text-gray-500">Cargando categorías…</p>
    <p v-else-if="!categories.length" class="py-8 text-center text-sm text-gray-500">No hay categorías.</p>

    <ul v-else class="space-y-3">
      <li
        v-for="c in categories"
        :key="c.id"
        class="bg-white border border-gray-200 rounded-lg px-4 py-3 flex items-center justify-between gap-3"
      >
        <div class="min-w-0">
          <div class="flex flex-wrap items-center gap-2">
            <h3 class="text-base font-medium text-gray-800">{{ c.name }}</h3>
            <span class="px-2 py-0.5 text-xs rounded bg-indigo-100 text-indigo-800 font-mono">{{ c.slug }}</span>
            <span :class="['px-2 py-0.5 text-xs rounded', c.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700']">
              {{ c.active ? 'Activa' : 'Inactiva' }}
            </span>
          </div>
          <p v-if="c.description" class="mt-1 text-sm text-gray-600">{{ c.description }}</p>
        </div>
        <div class="flex items-center gap-3 shrink-0">
          <button
            type="button"
            :disabled="togglingId === c.id"
            class="text-sm text-yellow-600 hover:text-yellow-700 disabled:opacity-50"
            @click="toggle(c)"
          >
            {{ c.active ? 'Desactivar' : 'Activar' }}
          </button>
          <button type="button" class="text-blue-500 hover:text-blue-700" title="Editar" aria-label="Editar categoría" @click="openForm(c)">
            <i class="fa-regular fa-pen-to-square"></i>
          </button>
          <button type="button" class="text-red-500 hover:text-red-700" title="Eliminar" aria-label="Eliminar categoría" @click="pendingDelete = c">
            <i class="fa-regular fa-trash-can"></i>
          </button>
        </div>
      </li>
    </ul>

    <!-- Modal crear/editar -->
    <div
      v-if="formOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-gray-600 bg-opacity-50 p-4"
      @click.self="closeForm"
    >
      <div
        class="bg-white rounded-lg shadow-xl w-full max-w-md max-h-full overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="categoria-form-title"
      >
        <div class="flex items-center justify-between px-6 pt-5 pb-3">
          <h2 id="categoria-form-title" class="text-lg font-semibold text-gray-800">
            {{ editingId ? 'Editar Categoría' : 'Nueva Categoría' }}
          </h2>
          <button type="button" class="text-gray-400 hover:text-gray-600 text-xl leading-none" aria-label="Cerrar" @click="closeForm">
            &times;
          </button>
        </div>
        <form class="px-6 pb-6 space-y-4" @submit.prevent="save">
          <p v-if="formError" class="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{{ formError }}</p>

          <div>
            <label class="block text-sm text-gray-600 mb-1" for="categoria-name">Nombre *</label>
            <input
              id="categoria-name"
              v-model="form.name"
              type="text"
              maxlength="100"
              class="w-full border-gray-200 rounded-md text-sm focus:border-indigo-600 focus:ring-indigo-500"
              @input="syncSlug"
            />
          </div>
          <div>
            <label class="block text-sm text-gray-600 mb-1" for="categoria-slug">Slug *</label>
            <input
              id="categoria-slug"
              v-model="form.slug"
              type="text"
              maxlength="100"
              class="w-full border-gray-200 rounded-md text-sm font-mono focus:border-indigo-600 focus:ring-indigo-500"
              @input="slugTouched = true"
            />
            <p class="mt-1 text-xs text-gray-500">Solo minúsculas, números y guiones (ej: eventos-escolares).</p>
          </div>
          <div>
            <label class="block text-sm text-gray-600 mb-1" for="categoria-description">Descripción</label>
            <textarea
              id="categoria-description"
              v-model="form.description"
              rows="3"
              class="w-full border-gray-200 rounded-md text-sm focus:border-indigo-600 focus:ring-indigo-500"
            />
          </div>

          <div class="flex justify-end gap-2 pt-2">
            <button
              type="button"
              :disabled="saving"
              class="px-4 py-2 text-sm text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 disabled:opacity-50"
              @click="closeForm"
            >
              Cancelar
            </button>
            <button
              type="submit"
              :disabled="saving"
              class="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {{ saving ? 'Guardando…' : editingId ? 'Actualizar' : 'Crear' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <ConfirmAction
      :is-visible="!!pendingDelete"
      title="Eliminar categoría"
      :description="deleteDescription"
      action="delete"
      :processing="deleting"
      @confirm="confirmDelete"
      @cancel="pendingDelete = null"
    />
  </section>
</template>

<script>
import { blogCategoriesApi } from '@/service';
import { SLUG_PATTERN, apiErrorMessage, slugify } from '@/utils/blog';

const emptyForm = () => ({ name: '', slug: '', description: '' });

export default {
  name: 'NoticiaCategories',
  data() {
    return {
      categories: [],
      loading: false,
      error: '',
      togglingId: null,
      formOpen: false,
      editingId: null,
      form: emptyForm(),
      // Mientras no se edite el slug a mano, sigue al nombre.
      slugTouched: false,
      formError: '',
      saving: false,
      pendingDelete: null,
      deleting: false,
    };
  },
  computed: {
    deleteDescription() {
      return this.pendingDelete
        ? `¿Eliminar la categoría "${this.pendingDelete.name}"? Las noticias asociadas quedarán sin categoría.`
        : '';
    },
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = '';
      try {
        const { data } = await blogCategoriesApi.list();
        this.categories = data.data || [];
      } catch (e) {
        this.error = apiErrorMessage(e, 'No se pudieron cargar las categorías.');
      } finally {
        this.loading = false;
      }
    },
    openForm(category) {
      this.editingId = category ? category.id : null;
      this.form = category
        ? { name: category.name, slug: category.slug, description: category.description || '' }
        : emptyForm();
      this.slugTouched = !!category;
      this.formError = '';
      this.formOpen = true;
    },
    closeForm() {
      if (!this.saving) this.formOpen = false;
    },
    syncSlug() {
      if (!this.slugTouched) this.form.slug = slugify(this.form.name);
    },
    async save() {
      this.formError = '';
      const name = this.form.name.trim();
      const slug = this.form.slug.trim();
      if (!name) {
        this.formError = 'El nombre es obligatorio.';
        return;
      }
      if (!SLUG_PATTERN.test(slug)) {
        this.formError = 'El slug es obligatorio y solo admite minúsculas, números y guiones (ej: eventos-escolares).';
        return;
      }
      const body = { name, slug, description: this.form.description.trim() };
      this.saving = true;
      try {
        if (this.editingId) {
          await blogCategoriesApi.update(this.editingId, body);
        } else {
          await blogCategoriesApi.create(body);
        }
        this.formOpen = false;
        await this.load();
      } catch (e) {
        this.formError = apiErrorMessage(e, 'No se pudo guardar la categoría.');
      } finally {
        this.saving = false;
      }
    },
    async toggle(category) {
      this.togglingId = category.id;
      this.error = '';
      try {
        await blogCategoriesApi.toggleActive(category.id);
        await this.load();
      } catch (e) {
        this.error = apiErrorMessage(e, 'No se pudo cambiar el estado de la categoría.');
      } finally {
        this.togglingId = null;
      }
    },
    async confirmDelete() {
      this.deleting = true;
      try {
        await blogCategoriesApi.remove(this.pendingDelete.id);
        this.pendingDelete = null;
        await this.load();
      } catch (e) {
        this.pendingDelete = null;
        this.error = apiErrorMessage(e, 'No se pudo eliminar la categoría.');
      } finally {
        this.deleting = false;
      }
    },
  },
};
</script>
